import { useState } from 'react';

import { copy } from '../../core/copy';
import { inboxFor, type InboxItem, type PeerWork } from '../../core/posada';
import { unlockedFeatures } from '../../core/rules';
import { relativeDays } from '../../core/time';
import { Btn } from '../components';
import { sfx } from '../audio';
import {
  contentOf,
  leaveComment,
  openPeer,
  playerOf,
  posadaOf,
  replyToComment,
  toast,
  useStore,
} from '../store';

/**
 * The two halves of the round trip, as small pieces that live wherever the moment happens.
 *
 * Neither of them holds a word of the critique. It is on Skool, on the post, where the community
 * already reads and moderates it — everything here is a pointer to it and the four answers a
 * person can give back.
 */

/* ------------------------------------------------------------------ receiving */

/**
 * The comments left on one of my works, shown at the exact moment they matter: on the card that
 * is about to ask me to revise. It is not an inbox and there is no screen for it — the revision
 * was already going to be my next step (`nextStep` promotes it), so this attaches a name and a
 * link to a card that existed anyway. Nobody has to learn a new place to look.
 */
export function PrimljeniKomentari({ nodeId }: { nodeId: string }) {
  const posada = useStore(posadaOf);
  const items = inboxFor(posada, nodeId);
  if (items.length === 0) return null;
  return (
    <>
      {items.map((item) => (
        <Komentar key={item.id} item={item} />
      ))}
    </>
  );
}

function Komentar({ item }: { item: InboxItem }) {
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // „Vrati komentar" leads to the crew screen, so it is only offered to somebody who has one.
  // A day-one member still gets the comment, the link and the thank-you; they simply are not
  // handed a door to a room that has not appeared yet (GDD §3).
  const canGoBack = unlockedFeatures(content, player).includes('posada');

  const reply = async (action: 'hvala' | 'nema') => {
    setBusy(true);
    setError(null);
    const result = await replyToComment(item.id, action);
    setBusy(false);
    if (!result.ok) {
      sfx.error();
      setError(result.message);
    }
  };

  return (
    <div className="timeline-note">
      <b>{copy.posada.gotTitle}</b>
      <div className="who">{copy.posada.from(item.fromName)}</div>
      <p className="tiny" style={{ marginTop: 6 }}>
        {copy.posada.gotLead}
      </p>

      <div className="btn-row" style={{ marginTop: 10 }}>
        {/* The post the comment was left on, snapshotted when it was left. Guarded rather than
            assumed: an empty href silently reloads the app, which is the most confusing possible
            answer to „show me the comment". */}
        {item.url ? (
          <a className="btn ghost small" href={item.url} target="_blank" rel="noreferrer noopener">
            {copy.posada.seeComment}
          </a>
        ) : null}
        {item.thankedAt ? (
          <span className="tiny muted">{copy.posada.thanked}</span>
        ) : (
          <Btn variant="ghost" size="small" disabled={busy} onClick={() => void reply('hvala')}>
            {copy.posada.thanks}
          </Btn>
        )}
        {canGoBack ? (
          <Btn variant="ghost" size="small" onClick={() => openPeer(item.fromId)}>
            {copy.posada.giveBack}
          </Btn>
        ) : null}
      </div>

      {item.missingAt ? (
        <p className="tiny muted" style={{ marginTop: 8 }}>
          {copy.posada.missingDone}
        </p>
      ) : (
        <button
          className="linky tiny muted"
          disabled={busy}
          onClick={() => void reply('nema')}
          title={copy.posada.missingAsk}
        >
          {copy.posada.missing}
        </button>
      )}

      {error ? (
        <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ giving */

/**
 * One work somebody is waiting on, and the two steps that answer it: open their post and write the
 * critique where it belongs, then come back and say so.
 *
 * The second button is a claim, not a proof — nothing here can check Skool (GDD §8.1). What makes
 * it safe is that it is aimed at one named person who is about to go and look, and „nema ničega
 * tamo" is one tap away on their side.
 */
export function RadRed({ work, onDone }: { work: PeerWork; onDone?: () => void }) {
  const content = useStore(contentOf);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  const node = content.islands.flatMap((i) => i.nodes).find((n) => n.id === work.nodeId);

  const claim = async () => {
    setBusy(true);
    setError(null);
    const result = await leaveComment(work.memberId, work.nodeId);
    setBusy(false);
    if (result.ok) {
      toast(copy.posada.commentSent, copy.posada.commentSentBody, 'green');
      onDone?.();
    } else {
      sfx.error();
      setError(result.message);
    }
  };

  return (
    <div className="peer-work">
      <div className="peer-work-head">
        <b>{node?.title ?? work.nodeId}</b>
        <span className="tiny muted">
          {work.name} · {copy.posada.posted(relativeDays(work.postedAt, Date.now()))}
          {work.answered ? ` · ${copy.posada.answered}` : ''}
        </span>
      </div>
      <div className="btn-row" style={{ marginTop: 8 }}>
        <a
          className="btn primary small"
          href={work.url}
          target="_blank"
          rel="noreferrer noopener"
          onClick={() => setOpened(true)}
        >
          {copy.posada.openPost}
        </a>
        {/*
          Deliberately not disabled until the post has been opened — somebody may well have read it
          on their phone an hour ago. The greyed-out state is a hint about the order of operations,
          not a lock, so the button becomes available the moment they touch the link.
        */}
        <Btn
          variant={opened ? 'gold' : 'ghost'}
          size="small"
          disabled={busy || work.claimed}
          onClick={() => void claim()}
        >
          {copy.posada.commented}
        </Btn>
      </div>
      <p className="tiny muted" style={{ marginTop: 6 }}>
        {copy.posada.commentHint}
      </p>
      {error ? (
        <p className="tiny" style={{ marginTop: 6, color: 'var(--warn)' }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
